import { useParams, Link } from 'react-router-dom';
import Navbar from '@/components/feature/Navbar';
import Footer from '@/components/feature/Footer';
import Breadcrumb from '@/components/feature/Breadcrumb';
import SEOMeta from '@/components/feature/SEOMeta';
import SchemaOrg from '@/components/feature/SchemaOrg';
import { articles } from '@/mocks/articles';

export default function ArticlePage() {
  const { id } = useParams<{ id: string }>();
  const article = articles.find((a) => a.id === Number(id));

  if (!article) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-16 flex items-center justify-center">
          <div className="text-center">
            <div className="w-20 h-20 mx-auto mb-6 flex items-center justify-center rounded-full bg-background-100">
              <i className="ri-file-unknow-line text-foreground-400 text-3xl"></i>
            </div>
            <h1 className="text-xl font-heading font-bold text-foreground-900 mb-2">文章不存在</h1>
            <p className="text-foreground-500 mb-6">请检查文章链接是否正确</p>
            <Link
              to="/"
              className="whitespace-nowrap px-6 py-2.5 text-sm font-medium rounded-full bg-primary-500 text-background-50 hover:bg-primary-600 transition-colors duration-200"
            >
              返回首页
            </Link>
          </div>
        </main>
        <Footer />
      </div>
    );
  }

  const relatedArticles = articles.filter((a) => a.id !== article.id).slice(0, 3);

  return (
    <>
      <SEOMeta
        title={article.title}
        description={article.excerpt}
        keywords={article.tags.join(',')}
        canonicalUrl={`https://rwgjqtpido.cn/article/${article.id}`}
        ogType="article"
        ogImage={article.image}
        articlePublishedTime={article.date}
      />
      <SchemaOrg
        type="Article"
        data={{
          '@context': 'https://schema.org',
          '@type': 'Article',
          headline: article.title,
          description: article.excerpt,
          image: article.image,
          datePublished: article.date,
          author: {
            '@type': 'Person',
            name: article.author,
          },
          publisher: {
            '@type': 'Organization',
            name: '紅桃視頻',
          },
        }}
      />
      <SchemaOrg
        type="BreadcrumbList"
        data={{
          '@context': 'https://schema.org',
          '@type': 'BreadcrumbList',
          itemListElement: [
            { '@type': 'ListItem', position: 1, name: '首页', item: 'https://rwgjqtpido.cn' },
            { '@type': 'ListItem', position: 2, name: '资讯', item: 'https://rwgjqtpido.cn/article/1' },
            { '@type': 'ListItem', position: 3, name: article.title },
          ],
        }}
      />

      <div className="min-h-screen flex flex-col">
        <Navbar />
        <main className="flex-1 pt-16">
          <Breadcrumb
            items={[
              { label: '首页', path: '/' },
              { label: '资讯', path: '/article/1' },
              { label: article.title },
            ]}
          />

          <article className="py-10 md:py-14">
            <div className="px-4 md:px-6 max-w-4xl mx-auto">
              <div className="mb-8">
                <div className="flex items-center gap-2 flex-wrap mb-4">
                  {article.tags.map((tag) => (
                    <span
                      key={tag}
                      className="whitespace-nowrap px-3 py-1 rounded-full text-xs font-medium bg-secondary-100 text-secondary-700"
                    >
                      {tag}
                    </span>
                  ))}
                </div>
                <h1 className="text-2xl md:text-3xl lg:text-4xl font-heading font-bold text-foreground-900 mb-4 leading-tight">
                  {article.title}
                </h1>
                <div className="flex flex-col sm:flex-row sm:items-center gap-3 sm:gap-6 text-sm text-foreground-500">
                  <div className="flex items-center gap-2">
                    <div className="w-8 h-8 rounded-full overflow-hidden">
                      <img src={article.authorAvatar} alt={article.author} className="w-full h-full object-cover" />
                    </div>
                    <span className="font-medium text-foreground-700">{article.author}</span>
                  </div>
                  <span className="hidden sm:block text-foreground-300">|</span>
                  <span>{article.date}</span>
                  <span className="hidden sm:block text-foreground-300">|</span>
                  <span>阅读时间 {article.readTime}</span>
                </div>
              </div>

              <div className="mb-10 rounded-lg overflow-hidden">
                <img
                  src={article.image}
                  alt={article.title}
                  title={article.title}
                  className="w-full aspect-video object-cover"
                />
              </div>

              <div
                className="prose prose-lg max-w-none
                  prose-headings:font-heading prose-headings:text-foreground-900 prose-headings:font-bold
                  prose-h2:text-xl md:prose-h2:text-2xl prose-h2:mt-10 prose-h2:mb-4
                  prose-p:text-foreground-700 prose-p:leading-relaxed prose-p:mb-5
                  prose-strong:text-foreground-900 prose-strong:font-semibold
                  prose-ul:text-foreground-700 prose-li:mb-2
                  [&_ul]:list-disc [&_ul]:pl-5"
                dangerouslySetInnerHTML={{ __html: article.content }}
              />

              <div className="mt-12 pt-8 border-t border-background-200/70">
                <div className="flex items-center justify-between">
                  <div className="flex items-center gap-2">
                    <div className="w-10 h-10 rounded-full overflow-hidden">
                      <img src={article.authorAvatar} alt={article.author} className="w-full h-full object-cover" />
                    </div>
                    <div>
                      <p className="text-sm font-medium text-foreground-900">{article.author}</p>
                      <p className="text-xs text-foreground-500">紅桃視頻创作者</p>
                    </div>
                  </div>
                  <div className="flex items-center gap-2">
                    <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-primary-100 hover:text-primary-600 transition-colors duration-200 text-foreground-600">
                      <i className="ri-thumb-up-line text-lg"></i>
                    </button>
                    <button className="w-9 h-9 flex items-center justify-center rounded-full bg-background-100 hover:bg-accent-100 hover:text-accent-600 transition-colors duration-200 text-foreground-600">
                      <i className="ri-share-forward-line text-lg"></i>
                    </button>
                  </div>
                </div>
              </div>
            </div>
          </article>

          {relatedArticles.length > 0 && (
            <section className="py-10 md:py-14 bg-background-100">
              <div className="px-4 md:px-6 max-w-4xl mx-auto">
                <h2 className="text-xl md:text-2xl font-heading font-bold text-foreground-900 mb-6">
                  相关文章
                </h2>
                <div className="grid grid-cols-1 sm:grid-cols-3 gap-4 md:gap-5">
                  {relatedArticles.map((ra) => (
                    <Link
                      key={ra.id}
                      to={`/article/${ra.id}`}
                      onClick={() => window.scrollTo({ top: 0, behavior: 'smooth' })}
                      className="group block rounded-lg overflow-hidden bg-background-50 hover:bg-background-200/70 transition-all duration-200"
                    >
                      <div className="aspect-video overflow-hidden">
                        <img
                          src={ra.image}
                          alt={ra.title}
                          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
                          loading="lazy"
                        />
                      </div>
                      <div className="p-4">
                        <h3 className="text-sm font-medium text-foreground-900 line-clamp-2 mb-2 group-hover:text-primary-600 transition-colors duration-200">
                          {ra.title}
                        </h3>
                        <div className="flex items-center gap-3 text-xs text-foreground-500">
                          <span>{ra.date}</span>
                          <span>{ra.readTime}</span>
                        </div>
                      </div>
                    </Link>
                  ))}
                </div>
              </div>
            </section>
          )}
        </main>
        <Footer />
      </div>
    </>
  );
}